import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';

import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import {
  BehaviorSubject,
  Observable,
  filter,
  firstValueFrom,
  map,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { StoreService } from '../../../../store.service';

import { GetRemindersByObjectIdGQL } from '@etoh/database/angular';
import { StoreDataTypeKey } from '@etoh/database/core';
import {
  PageFormButton,
  PageFormMode,
  PageFormQueryParamsAction,
  isShowPageFormButton,
} from '../../../interfaces/pageForm.interface';
import { TabsTable } from '../../../interfaces/tabsTable.interface';

import { Result } from '@etoh/database/core';

import { omit, pick } from 'lodash';

import { environment } from '../../../../../../environments/environment';
import { RemindersUpsertComponent } from '../../../../reminders/containers/reminders-upsert/reminders-upsert.component';
import { FormsService } from '../../../forms/forms.service';

import { CommonModule } from '@angular/common';
import { FlexLayoutModule } from '@angular/flex-layout';
import { FormlyModule } from '@ngx-formly/core';
import { UserService } from 'apps/desktop/src/app/user.service';
import copy from 'fast-copy';
import { NzAffixModule } from 'ng-zorro-antd/affix';
import { NzBackTopModule } from 'ng-zorro-antd/back-top';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropDownModule } from 'ng-zorro-antd/dropdown';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzPopoverModule } from 'ng-zorro-antd/popover';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { EntitiesService } from '../../../../entities/entities.service';
import { HistoryTimelineComponent } from '../../../../history/history-timeline/history-timeline.component';
import { BackButtonComponent } from '../back-button/back-button.component';
import { TabsTableComponent } from '../tabs-table/tabs-table.component';
import { getAddLink } from './page-form.pure';

const TextsMode = {
  edit: {
    header: 'Edit',
    button: 'Save',
  },
  add: {
    header: 'Add a new',
    button: 'Add',
  },
};

@Component({
  selector: 'etoh-page-form',
  templateUrl: './page-form.component.html',
  styleUrls: ['./page-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    HistoryTimelineComponent,
    NzAffixModule,
    NzButtonModule,
    ReactiveFormsModule,
    NzPopoverModule,
    TabsTableComponent,
    HistoryTimelineComponent,
    FormlyModule,
    NzDropDownModule,
    RouterModule,
    NzSpinModule,
    NzBackTopModule,
    BackButtonComponent,
    FlexLayoutModule,
    NzIconModule,
    NzFormModule,
  ],
})
export class PageFormComponent implements OnInit, OnChanges, AfterViewInit {
  isProd = environment.production;
  @Input() id: number;
  // Only used for user
  @Input() idType: 'number' | 'string' = 'number';

  @Input() dataType: StoreDataTypeKey;
  @Input() fields: FormlyFieldConfig[];

  @Input() title?: string;
  @Input() name: string;

  @Input() headerButtons: PageFormButton[] = [];
  displayedHeaderButtons: PageFormButton[] = [];
  @Input() hideHeaderButtons = false;

  @Input() keysOnlyForSaving: string[] = [];

  get extraButtons(): PageFormButton[] {
    if (!this.id) {
      return [];
    }

    return (<PageFormButton[]>[
      {
        label: 'Unarchive',
        visibity: ['edit'],
        isShow: () => !!this.model.archivedAt,
        roles: ['user'],
        click: () => {
          this.entitiesService
            .unarchive(this.dataType, [this.id])
            .subscribe((res) => {
              if (!res) {
                return;
              }

              this.model = {
                ...this.model,
                archivedAt: null,
              };
            });
        },
      },
      {
        label: 'Archive',
        visibity: ['edit'],
        isShow: () => this.model.archivedAt === null,
        click: () => {
          this.entitiesService
            .archivate(this.dataType, [this.id])
            .subscribe((res) => {
              if (!res) {
                return;
              }

              this.model = {
                ...this.model,
                archivedAt: new Date().toISOString(),
              };
            });
        },
      },
      {
        label: 'Delete',
        visibity: ['edit'],
        isShow: () => !this.model.deletedAt,
        roles: ['user'],
        click: () => {
          this.entitiesService
            .delete(this.dataType, [this.id])
            .subscribe((res) => {
              if (!res) {
                return;
              }

              this.model = {
                ...this.model,
                deletedAt: new Date().toISOString(),
              };
            });
        },
      },
    ])
      .filter((button) => {
        const user = this.userService.userBehaviourSubject$.value;

        return user ? isShowPageFormButton(button, user) : true;
      })
      .filter((r) => (r.isShow ? r.isShow() : true));
  }

  @Input() dataTabs: TabsTable[] = [];
  displayedDataTabs: TabsTable[] = [];

  remindersUpdate = new BehaviorSubject<null>(null);

  @Input() afterGetModel?: (model: any) => Observable<any>;

  // Should be arrow function for this
  @Input() insertFunction?: (model: any) => Observable<any>;
  @Input() updateFunction?: (model: any) => Observable<any>;

  @Input() shouldPrevent?: (
    model: any,
    mode: PageFormMode
  ) => Observable<boolean>;

  // Force message to override the default one
  @Input() messageToShow: string;

  @Output() loaded = new EventEmitter<boolean>();

  // emit id field
  @Output() saved = new EventEmitter<number>();

  // override default mode
  @Input() forceMode: PageFormMode;

  get mode(): PageFormMode {
    if (this.forceMode) {
      return this.forceMode;
    }

    if (this.id) {
      return 'edit';
    }
    return 'add';
  }

  // If we are on the page
  @Input() standalone = true;

  loading = true;
  get textMode() {
    return TextsMode[this.mode];
  }

  submitLoading = false;

  @Input() options: FormlyFormOptions = {};

  @Input() form = new FormGroup({});

  initialModel: any;
  @Input() model: any = {};

  @Input() hasSubmitButton = true;

  @Input() duplicateKeysToOmit: string[] = [];

  @Input() afterCreate: 'redirectToList' | 'stayOnPage' = 'stayOnPage';
  listLinkOverride?: string;
  listLink: string;

  @Input() hasExtraSubmitButton = false;

  @Input() disabled = false;

  submitButtons: any[] = [
    {
      label: 'Duplicate',
      visibity: ['add', 'edit'],
      afterSubmit: (id: number) => {
        this.router.navigate([getAddLink(this.dataType)], {
          queryParams: {
            duplicateId: id,
          },
        });
      },
    },
    {
      label: 'New',
      visibity: ['add', 'edit'],
      afterSubmit: (id: number) => {
        this.router.navigate([getAddLink(this.dataType)]);
      },
    },
  ];

  targetForAffix: any;

  public cd = inject(ChangeDetectorRef);
  private message = inject(NzMessageService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private storeService = inject(StoreService);
  private getRemindersByObjectIdGQL = inject(GetRemindersByObjectIdGQL);
  private formsService = inject(FormsService);
  private elRef = inject(ElementRef);
  private entitiesService = inject(EntitiesService);
  public userService = inject(UserService);

  constructor() {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  async ngOnInit() {
    this.loading = true;

    this.cd.detach();
    let duplicateId = null;

    const initialModelFromUrlString = this.standalone
      ? this.route.snapshot.queryParams['model']
        ? JSON.parse(this.route.snapshot.queryParams['model'])
        : null
      : null;

    if (this.standalone) {
      this.id =
        this.idType === 'number'
          ? +this.route.snapshot.params['id']
          : this.route.snapshot.params['id'];
      duplicateId = +this.route.snapshot.queryParams['duplicateId'];

      this.listLinkOverride =
        this.route.snapshot.queryParams['listLinkOverride'] ?? undefined;

      this.listLink = this.listLinkOverride ?? `/features/${this.dataType}`;

      if (initialModelFromUrlString) {
        this.model = initialModelFromUrlString;
        this.triggerChangesOnForm();
      }
    }

    if (this.id) {
      this.model = await this.getModel(this.id);

      if (!this.model) {
        this.router.navigateByUrl(getAddLink(this.dataType));
      }
    } else if (duplicateId) {
      console.log('initialModelFromUrlString', initialModelFromUrlString);

      const model = await this.getModel(duplicateId);
      this.model = {
        ...omit(model, [
          'id',
          'createdAt',
          'updatedAt',
          'archivedAt',
          'deletedAt',
          '__typename',
          ...this.duplicateKeysToOmit,
        ]),
        ...(initialModelFromUrlString ?? {}),
      };
    }

    this.loading = false;
    this.cd.reattach();
    this.cd.detectChanges();

    this.initialModel = copy(this.model);

    this.loaded.emit(true);

    this.constructTabs();
    this.constructHeaders();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['dataTabs']) {
      this.constructTabs();
    }

    if (changes['headerButtons']) {
      this.constructHeaders();
    }

    if (changes['disabled']) {
      if (this.disabled) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    }
  }

  triggerChangesOnForm() {
    setTimeout(() => {
      this.formsService.triggerChangesOnForm(this.fields);
      this.cd.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    //Called after ngAfterContentInit when the component's view has been initialized. Applies to components only.
    //Add 'implements AfterViewInit' to the class.
    this.targetForAffix = this.getTargetForAffix();
  }

  private getModel(id: number): Promise<any> {
    const x: any[] = [];
    return firstValueFrom(
      this.storeService.getObjectFromStoreById<any>(this.dataType, id).pipe(
        tap((model) => {
          if (!model) {
            x.push(this.message.info('Waiting for data to load...'));
          } else {
            x.forEach((m) => this.message.remove(m));
          }
        }),
        filter((model) => {
          return !!model;
        }),
        take(1),
        map((res) => copy(res)),
        switchMap((res) => {
          if (this.afterGetModel) {
            return this.afterGetModel(res);
          }

          return of(res);
        })
      )
    );
  }

  private setId(id: number) {
    this.id = id;

    this.model = {
      ...this.model,
      id,
    };

    this.constructTabs();
    this.constructHeaders();
  }

  public constructTabs() {
    if (this.forceMode) {
      return;
    }

    if (this.mode === 'edit') {
      this.displayedDataTabs = [
        ...(this.dataTabs || []),
        {
          title: 'Reminders',
          dataType: 'reminders',
          datas$: this.remindersUpdate.pipe(
            switchMap(() => {
              return this.getRemindersByObjectIdGQL
                .fetch(
                  {
                    objectId: this.id,
                    table: this.dataType,
                  },
                  {
                    context: {
                      disableGlobalError: true,
                    },
                  }
                )
                .pipe(map((res) => res.data.reminders));
            })
          ),
        },
      ];
    } else {
      this.displayedDataTabs = [...(this.dataTabs || [])];
    }
  }

  public constructHeaders() {
    if (this.forceMode) {
      return;
    }

    let displayedHeaderButtons: PageFormButton[] = [];
    if (this.mode === 'edit') {
      if (this.standalone) {
        displayedHeaderButtons = [
          ...(this.headerButtons || []),
          {
            visibity: ['edit'],
            label: 'Add a reminder',
            roles: ['user'],
            click: () => {
              this.formsService
                .openModalPageForm(
                  RemindersUpsertComponent,
                  {
                    model: {
                      objectId: this.id,
                      table: this.dataType,
                    },
                  },
                  {
                    nzTitle: 'Add a reminder',
                    nzWidth: '300px',
                  }
                )
                .afterClose.subscribe((res) => {
                  console.log('🔥', res);
                  if (res) {
                    this.remindersUpdate.next(null);
                  }
                });
            },
          },
          {
            visibity: ['edit'],
            label: 'Duplicate',
            roles: ['user'],
            link: getAddLink(this.dataType),
            queryParams: { duplicateId: this.id },
          },
        ];
      } else {
        displayedHeaderButtons = [...(this.headerButtons || [])];
      }
    } else {
      displayedHeaderButtons = [...(this.headerButtons || [])];
    }

    this.displayedHeaderButtons = displayedHeaderButtons.filter((b) => {
      const user = this.userService.userBehaviourSubject$.value;

      return user ? isShowPageFormButton(b, user) : true;
    });
  }

  public submitAndGoBackToList() {
    this.submit(() => {
      this.goBackToList();
    });
  }

  public async submit(afterSubmit: ((res: any) => void) | null = null) {
    console.log('submit', afterSubmit);
    this.submitLoading = true;

    if (this.shouldPrevent) {
      const shouldPrevent = await firstValueFrom(
        this.shouldPrevent(this.model, this.mode).pipe(take(1))
      );

      if (shouldPrevent) {
        this.submitLoading = false;
        this.cd.detectChanges();
        return;
      }
    }

    const modelToSave = this.keysOnlyForSaving?.length
      ? pick(this.model, this.keysOnlyForSaving)
      : this.model;
    if (this.mode === 'add') {
      (this.insertFunction
        ? this.insertFunction(modelToSave)
        : this.storeService.addObject<any>(this.dataType, modelToSave)
      ).subscribe({
        next: (res: Result<any>) => {
          this.message.create(
            'success',
            this.messageToShow ?? `Has been added successfully`
          );
          this.doActionsFromUrl();
          this.submitLoading = false;
          const id = res?.returning?.[0]?.['id'];
          this.saved.emit(id);

          if (afterSubmit) {
            afterSubmit(id);
            return;
          }

          if (this.standalone) {
            if (this.afterCreate === 'redirectToList') {
              this.goBackToList();
            } else {
              this.setId(id);
              this.loaded.emit(true);
              this.scrollToTop();
              this.cd.detectChanges();
            }
          }
        },
        error: (err) => {
          console.error('pageForm error', err);
          this.submitLoading = false;
          this.cd.detectChanges();
        },
      });
    } else if (this.mode === 'edit') {
      (this.updateFunction
        ? this.updateFunction(modelToSave)
        : this.storeService.updateObjectByPk<any>(
            this.dataType,
            this.model.id,
            modelToSave
          )
      ).subscribe({
        next: (res) => {
          this.message.create(
            'success',
            this.messageToShow ?? `Has been updated successfully`
          );
          this.doActionsFromUrl();
          this.submitLoading = false;
          this.saved.emit(this.model.id);
          this.cd.detectChanges();

          if (afterSubmit) {
            afterSubmit(this.id);
            return;
          }
        },
        error: (err) => {
          this.submitLoading = false;
          this.cd.detectChanges();
        },
      });
    }
  }

  public doActionsFromUrl() {
    const initialActionsFromUrlString =
      this.route.snapshot.queryParams['actions'];
    if (!initialActionsFromUrlString?.length) {
      return;
    }

    const actions: PageFormQueryParamsAction[] = JSON.parse(
      initialActionsFromUrlString
    );

    const archive = (action: PageFormQueryParamsAction, id: number) => {
      this.storeService.archiveObjectByPk(action.dataType, id).subscribe({
        next: () => {
          this.message.create(
            'success',
            `${action.dataType} : has been archived successfully`
          );
        },
      });
    };

    actions.forEach((action) => {
      if (action.type === this.mode) {
        if (action.action === 'archive') {
          if (action.id) {
            archive(action, action.id);
          }

          if (action.ids?.length) {
            action.ids.forEach((id) => {
              archive(action, id);
            });
          }
        } else {
          console.warn('Unknown action', action);
        }
      }
    });
  }

  public why() {
    this.form.markAllAsTouched();

    (document as any)
      .querySelector('.ng-invalid')
      .scrollIntoView({ behavior: 'smooth' });
  }

  private getTargetForAffix() {
    return this.elRef.nativeElement?.closest('.mat-sidenav-content');
  }

  private scrollToTop() {
    const topClassName = '.ant-back-top';
    const top = document.querySelector(topClassName) as HTMLElement;
    top?.click();
  }

  public updateModel() {
    this.ngOnInit();
  }

  public goBackToList() {
    this.router.navigateByUrl(this.listLink);
  }
}
